/**
 * What's New Modal Component
 *
 * Displays changelog for a specific version or the latest release.
 * Shows automatically when there's a new version the user hasn't seen.
 */

import React, { useEffect, useCallback, useId } from 'react';
import { X, Sparkles, Wrench, Bug, ChevronRight } from 'lucide-react';
import type { ChangelogEntry } from '@/data/changelog';
import { getLatestVersion } from '@/data/changelog';
import { setLastSeenVersion } from './whatsNew.utils';

interface WhatsNewModalProps {
  isOpen: boolean;
  onClose: () => void;
  entry?: ChangelogEntry;
  onViewFullHistory?: () => void;
}

export const WhatsNewModal: React.FC<WhatsNewModalProps> = ({
  isOpen,
  onClose,
  entry,
  onViewFullHistory,
}) => {
  const changelogEntry = entry || getLatestVersion();
  const titleId = useId();

  // Mark version as seen when modal closes
  const handleClose = useCallback(() => {
    if (changelogEntry) {
      setLastSeenVersion(changelogEntry.version);
    }
    onClose();
  }, [changelogEntry, onClose]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !changelogEntry) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with gradient */}
          <div className="relative bg-gradient-to-br from-blue-500 via-blue-600 to-purple-600 px-6 py-8 text-white">
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Version badge */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 rounded-full text-sm font-medium mb-3">
              <Sparkles className="w-4 h-4" />
              <span>Версия {changelogEntry.version}</span>
            </div>

            <h2 id={titleId} className="text-2xl font-bold mb-2">{changelogEntry.title}</h2>
            <p className="text-white/80 text-sm">
              Издадена на {new Date(changelogEntry.date).toLocaleDateString('bg-BG', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {/* Highlights */}
            {changelogEntry.highlights.length > 0 && (
              <div className="mb-6">
                <ul className="space-y-2">
                  {changelogEntry.highlights.map((highlight, index) => (
                    <li
                      key={index}
                      className="flex items-center gap-2 text-gray-700 dark:text-gray-300"
                    >
                      <ChevronRight className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Features */}
            {changelogEntry.features && changelogEntry.features.length > 0 && (
              <div className="mb-6">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  <Sparkles className="w-4 h-4 text-blue-500" />
                  Нови функции
                </h3>
                <div className="space-y-3">
                  {changelogEntry.features.map((feature, index) => (
                    <div
                      key={index}
                      className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                    >
                      <div className="flex items-start gap-3">
                        {feature.icon && (
                          <span className="text-xl flex-shrink-0">{feature.icon}</span>
                        )}
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                            {feature.title}
                          </h4>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Improvements */}
            {changelogEntry.improvements && changelogEntry.improvements.length > 0 && (
              <div className="mb-6">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  <Wrench className="w-4 h-4 text-green-500" />
                  Подобрения
                </h3>
                <ul className="space-y-1.5">
                  {changelogEntry.improvements.map((improvement, index) => (
                    <li
                      key={index}
                      className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400"
                    >
                      <span className="text-green-500 mt-1">•</span>
                      <span>{improvement}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Bug Fixes */}
            {changelogEntry.bugfixes && changelogEntry.bugfixes.length > 0 && (
              <div>
                <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  <Bug className="w-4 h-4 text-orange-500" />
                  Поправки
                </h3>
                <ul className="space-y-1.5">
                  {changelogEntry.bugfixes.map((bugfix, index) => (
                    <li
                      key={index}
                      className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400"
                    >
                      <span className="text-orange-500 mt-1">•</span>
                      <span>{bugfix}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            {onViewFullHistory && (
              <button
                onClick={() => {
                  handleClose();
                  onViewFullHistory();
                }}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Виж пълната история
              </button>
            )}
            <button
              onClick={handleClose}
              className="ml-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Разбрах
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default WhatsNewModal;
